import React, { useState, useContext, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';
import dayjs from 'dayjs';
import fetchData from '../../helpers/fetchData';
import { tokens } from '../../theme';

// Interfaces for API data
interface DoiRateByCleanAffiliateData {
  clean_affiliate: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
}

interface DoiRateByEmailDomainData {
  email_domain: string;
  doi_rate: number | null;
  total_distinct_emails: number;
  confirmed_distinct_emails: number;
  email_percentage: number | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`affiliate-tabpanel-${index}`}
      aria-labelledby={`affiliate-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `affiliate-tab-${index}`,
    'aria-controls': `affiliate-tabpanel-${index}`,
  };
}

const DoiEvaluationAffiliatesPage = () => {
  const { filterState } = useContext(FilterContext)!;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tabValue, setTabValue] = useState(0);
  const [ratesByAffiliate, setRatesByAffiliate] = useState<DoiRateByCleanAffiliateData[]>([]);
  const [availableOriginHosts, setAvailableOriginHosts] = useState<string[]>([]);
  const [selectedOriginHostForFilter, setSelectedOriginHostForFilter] = useState<string>('all');
  const [selectedAffiliateForTable, setSelectedAffiliateForTable] = useState<string | null>(null);
  const [selectedEmailDomain, setSelectedEmailDomain] = useState<string | null>(null);

  const affiliateHeaderMapping: Record<string, HeaderMappingItem> = {
    clean_affiliate: { name: 'Clean Affiliate', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  const emailDomainHeaderMapping: Record<string, HeaderMappingItem> = {
    email_domain: { name: 'Email Domain', type: 'string' },
    doi_rate: { name: 'DOI Rate', type: 'percentage', digits: 2 },
    email_percentage: { name: '% of All Emails', type: 'percentage', digits: 2 },
    total_distinct_emails: { name: 'Total Emails', type: 'number' },
    confirmed_distinct_emails: { name: 'Confirmed Emails', type: 'number' },
  };

  // Compute summary for affiliate rates
  const computeAffiliateSummary = (
    data: DoiRateByCleanAffiliateData[],
  ): DoiRateByCleanAffiliateData => {
    if (!data.length) {
      return {
        clean_affiliate: 'Insgesamt',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
      };
    }

    const summary: DoiRateByCleanAffiliateData = {
      clean_affiliate: 'Insgesamt',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
    };

    // Sum up the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails || 0;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails || 0;
    });

    // Calculate the overall DOI rate from the totals
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Compute summary for email domain rates
  const computeEmailDomainSummary = (
    data: DoiRateByEmailDomainData[],
  ): DoiRateByEmailDomainData => {
    if (!data.length) {
      return {
        email_domain: 'Insgesamt',
        doi_rate: 0,
        total_distinct_emails: 0,
        confirmed_distinct_emails: 0,
        email_percentage: 1, // 100% of total
      };
    }

    const summary: DoiRateByEmailDomainData = {
      email_domain: 'Insgesamt',
      doi_rate: 0,
      total_distinct_emails: 0,
      confirmed_distinct_emails: 0,
      email_percentage: 1, // Will always be 100% of total
    };

    // Sum the totals
    data.forEach((item) => {
      summary.total_distinct_emails += item.total_distinct_emails;
      summary.confirmed_distinct_emails += item.confirmed_distinct_emails;
    });

    // Calculate the overall DOI rate
    summary.doi_rate =
      summary.total_distinct_emails > 0
        ? summary.confirmed_distinct_emails / summary.total_distinct_emails
        : 0;

    return summary;
  };

  // Fetch available origin hosts for filtering
  const fetchOriginHosts = useCallback(async (currentFilterState: IFilterState) => {
    if (!currentFilterState.startDate) return;

    try {
      const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
      const params: any = { startDate: formattedStartDate };

      if (currentFilterState.endDate) {
        params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
      }

      const response = await fetchData(`/api/doi-evaluation-affiliates/origin-hosts`, params);

      if (Array.isArray(response)) {
        setAvailableOriginHosts(['all', ...response]);
      } else {
        setAvailableOriginHosts(['all']);
      }
    } catch (error) {
      console.error('Error fetching origin hosts:', error);
      setAvailableOriginHosts(['all']);
    }
  }, []);

  // Fetch DOI rates by clean_affiliate
  const fetchAffiliateRates = useCallback(
    async (currentFilterState: IFilterState): Promise<DoiRateByCleanAffiliateData[]> => {
      if (!currentFilterState.startDate) return [];
      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = { startDate: formattedStartDate };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        if (selectedOriginHostForFilter && selectedOriginHostForFilter !== 'all') {
          params.originHost = selectedOriginHostForFilter;
        }

        const response = await fetchData(
          `/api/doi-evaluation-affiliates/rates-by-affiliate`,
          params,
        );

        // Handle response safely
        let resultArray: DoiRateByCleanAffiliateData[] = [];

        if (Array.isArray(response)) {
          resultArray = response.filter(
            (item) => item && typeof item === 'object' && 'clean_affiliate' in item,
          ) as DoiRateByCleanAffiliateData[];
        }

        setRatesByAffiliate(resultArray);
        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by clean affiliate:', error);
        setRatesByAffiliate([]);
        return [];
      }
    },
    [selectedOriginHostForFilter],
  );

  // Fetch DOI rates by email_domain for selected affiliate
  const fetchEmailDomainRates = useCallback(
    async (currentFilterState: IFilterState): Promise<DoiRateByEmailDomainData[]> => {
      if (!selectedAffiliateForTable || !currentFilterState.startDate) {
        return [];
      }

      try {
        const formattedStartDate = dayjs(currentFilterState.startDate).format('YYYY-MM-DD');
        const params: any = {
          cleanAffiliate: selectedAffiliateForTable,
          startDate: formattedStartDate,
        };

        if (currentFilterState.endDate) {
          params.endDate = dayjs(currentFilterState.endDate).format('YYYY-MM-DD');
        }

        if (selectedOriginHostForFilter && selectedOriginHostForFilter !== 'all') {
          params.originHost = selectedOriginHostForFilter;
        }

        const response = await fetchData(
          `/api/doi-evaluation-affiliates/rates-by-email-domain`,
          params,
        );

        // Handle response safely
        let resultArray: DoiRateByEmailDomainData[] = [];

        if (Array.isArray(response)) {
          resultArray = response.filter(
            (item) => item && typeof item === 'object' && 'email_domain' in item,
          ) as DoiRateByEmailDomainData[];
        }

        return resultArray;
      } catch (error) {
        console.error('Error fetching DOI rates by email domain:', error);
        return [];
      }
    },
    [selectedAffiliateForTable, selectedOriginHostForFilter],
  );

  const handleOriginHostFilterChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedOriginHostForFilter(value);

    // Clear selection when changing filter
    setSelectedAffiliateForTable(null);
    setSelectedEmailDomain(null);
  };

  const handleAffiliateRowClick = (rowData: DoiRateByCleanAffiliateData) => {
    if (rowData && rowData.clean_affiliate && rowData.clean_affiliate !== 'Insgesamt') {
      setSelectedAffiliateForTable(rowData.clean_affiliate);
    }
  };

  const handleEmailDomainRowClick = (rowData: DoiRateByEmailDomainData) => {
    if (rowData && rowData.email_domain && rowData.email_domain !== 'Insgesamt') {
      setSelectedEmailDomain(rowData.email_domain);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchOriginHosts(filterState);
  }, [filterState.startDate, filterState.endDate, fetchOriginHosts]);

  useEffect(() => {
    fetchAffiliateRates(filterState);
  }, [
    filterState.startDate,
    filterState.endDate,
    selectedOriginHostForFilter,
    fetchAffiliateRates,
  ]);

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" mb="20px">
        DOI-Raten nach Clean Affiliate
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="origin-host-filter-label">Origin Host Filter (optional)</InputLabel>
              <Select
                labelId="origin-host-filter-label"
                value={selectedOriginHostForFilter}
                label="Origin Host Filter (optional)"
                onChange={handleOriginHostFilterChange}
              >
                {availableOriginHosts.map((host) => (
                  <MenuItem key={host} value={host}>
                    {host === 'all' ? 'Alle Hosts' : host}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Affiliate DOI rate tabs">
          <Tab label="Tabellen" {...a11yProps(0)} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
              <Typography variant="h6" mb={2} fontWeight="bold">
                DOI-Raten nach Clean Affiliate
                {selectedOriginHostForFilter !== 'all' &&
                  ` - gefiltert nach: ${selectedOriginHostForFilter}`}
              </Typography>
              <TableWidget<DoiRateByCleanAffiliateData>
                key={`affiliate-table-${selectedOriginHostForFilter}`}
                getData={() => fetchAffiliateRates(filterState)}
                computeSummary={computeAffiliateSummary}
                detailsHeaderMapping={affiliateHeaderMapping}
                initialSortColumn="total_distinct_emails"
                sortOrder="desc"
                excelDownloadName="doi_rates_by_clean_affiliate"
                onRowClick={handleAffiliateRowClick}
              />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            {selectedAffiliateForTable ? (
              <Paper sx={{ p: 2, borderRadius: '10px', backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" mb={2} fontWeight="bold">
                  DOI-Raten nach Email-Domain für: {selectedAffiliateForTable}
                  {selectedOriginHostForFilter !== 'all' &&
                    ` (Host: ${selectedOriginHostForFilter})`}
                </Typography>
                <TableWidget<DoiRateByEmailDomainData>
                  key={`email-domain-table-${selectedAffiliateForTable}-${selectedOriginHostForFilter}`}
                  getData={() => fetchEmailDomainRates(filterState)}
                  computeSummary={computeEmailDomainSummary}
                  detailsHeaderMapping={emailDomainHeaderMapping}
                  initialSortColumn="total_distinct_emails"
                  sortOrder="desc"
                  excelDownloadName={`doi_rates_email_domain_affiliate_${selectedAffiliateForTable.replace(/[^a-z0-9]/gi, '_')}`}
                  onRowClick={handleEmailDomainRowClick}
                />
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  borderRadius: '10px',
                  backgroundColor: colors.primary[400],
                }}
              >
                <Typography>
                  Bitte einen Clean Affiliate aus der oberen Tabelle auswählen, um die Email Domain
                  Raten anzuzeigen.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default DoiEvaluationAffiliatesPage;
